import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, Loader2, ArrowLeft, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FieldDataEntryPage() {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    product_interest: '',
    investment_timeline: '',
    language_preference: '',
    notes: '',
    assigned_to: ''
  });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [error, setError] = useState('');

  const canAssignOwner = role && ['admin', 'owner', 'sales_manager'].includes(role);

  useEffect(() => {
    if (canAssignOwner) {
      fetchAssignableUsers();
    }
  }, [canAssignOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssignableUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/assignable-users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAssignableUsers(data);
      }
    } catch (err) {
      logger.error('Failed to fetch assignable users:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.phone && !formData.email) {
      setError('Please provide either a phone number or email address');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim() || null,
        phone: formData.phone || null,
        email: formData.email || null,
        product_interest: formData.product_interest || null,
        investment_timeline: formData.investment_timeline || null,
        language_preference: formData.language_preference || null,
        notes: formData.notes || null,
        assigned_to: formData.assigned_to || null
      };

      const response = await fetch(`${API_URL}/api/leads/field-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setLastCreated(data);
        setSubmitted(true);
        toast.success('Lead created successfully!');
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
        toast.error(data.detail || 'Failed to create lead');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnother = () => {
    setSubmitted(false);
    setLastCreated(null);
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      product_interest: '',
      investment_timeline: '',
      language_preference: '',
      notes: '',
      assigned_to: ''
    });
  };

  if (submitted) {
    return (
      <div className="max-w-md mx-auto px-4" data-testid="field-data-success">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/leads')}
          className="mb-4 pl-0 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Leads
        </Button>

        <Card className="border-0 shadow-lg">
          <CardContent className="pt-10 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Lead Created!
            </h2>
            <p className="text-slate-600 mb-6">
              {lastCreated?.first_name} has been added to the CRM.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/leads')}
                className="flex-1"
              >
                View All Leads
              </Button>
              <Button
                onClick={handleAddAnother}
                className="flex-1 bg-gold hover:bg-gold/90 text-slate-900"
                data-testid="add-another-btn"
              >
                Add Another Lead
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper to render assignable users in select
  const renderUserOptions = () => {
    return assignableUsers.map((u) => (
      <SelectItem key={u.user_id} value={u.user_id}>
        {u.name} ({u.role.replace('_', ' ')})
      </SelectItem>
    ));
  };

  return (
    <div className="max-w-md mx-auto px-4" data-testid="field-data-entry-page">
      <Button 
        variant="ghost" 
        onClick={() => navigate('/leads')}
        className="mb-4 pl-0 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Leads
      </Button>

      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-slate-900 text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Field Data Entry</CardTitle>
              <p className="text-sm text-slate-300 font-normal">Quick lead capture from the field</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-slate-700 font-medium">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="Enter first name"
                className="h-12 text-base"
                data-testid="input-first-name"
              />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-slate-700 font-medium">
                Last Name
              </Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Enter last name"
                className="h-12 text-base"
                data-testid="input-last-name"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700 font-medium">
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="h-12 text-base"
                data-testid="input-phone"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="you@example.com"
                className="h-12 text-base"
                data-testid="input-email"
              />
            </div>

            {/* Product Interest */}
            <div className="space-y-2">
              <Label htmlFor="product_interest" className="text-slate-700 font-medium">
                Product Interest
              </Label>
              <Select
                value={formData.product_interest}
                onValueChange={(value) => handleChange('product_interest', value)}
              >
                <SelectTrigger className="h-12 text-base" data-testid="select-product-interest">
                  <SelectValue placeholder="Select interest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Land Plot">Land Plot</SelectItem>
                  <SelectItem value="Apartment Unit">Apartment Unit</SelectItem>
                  <SelectItem value="Hotel Share">Hotel Share</SelectItem>
                  <SelectItem value="Not Sure">Not Sure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Investment Timeline */}
            <div className="space-y-2">
              <Label htmlFor="investment_timeline" className="text-slate-700 font-medium">
                Investment Timeline
              </Label>
              <Select
                value={formData.investment_timeline}
                onValueChange={(value) => handleChange('investment_timeline', value)}
              >
                <SelectTrigger className="h-12 text-base" data-testid="select-investment-timeline">
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-30 days">0-30 days</SelectItem>
                  <SelectItem value="30-90 days">30-90 days</SelectItem>
                  <SelectItem value="90+ days">90+ days</SelectItem>
                  <SelectItem value="Not Shared">Not Shared</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Language Preference */}
            <div className="space-y-2">
              <Label htmlFor="language_preference" className="text-slate-700 font-medium">
                Language Preference
              </Label>
              <Select
                value={formData.language_preference}
                onValueChange={(value) => handleChange('language_preference', value)}
              >
                <SelectTrigger className="h-12 text-base" data-testid="select-language">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bangla">Bangla</SelectItem>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Owner Assignment (Admin/SM only) */}
            {canAssignOwner && assignableUsers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="assigned_to" className="text-slate-700 font-medium">
                  <User className="w-4 h-4 inline mr-1" />
                  Assign To
                </Label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(value) => handleChange('assigned_to', value)}
                >
                  <SelectTrigger className="h-12 text-base" data-testid="select-owner">
                    <SelectValue placeholder="Select lead owner (default: you)" />
                  </SelectTrigger>
                  <SelectContent>
                    {renderUserOptions()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Leave empty to assign to yourself
                </p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-slate-700 font-medium">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional context or notes..."
                className="min-h-[80px] text-base resize-none"
                data-testid="textarea-notes"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full h-12 text-base bg-gold hover:bg-gold/90 text-slate-900 font-semibold"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Lead...
                </>
              ) : (
                'Create Lead'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
